import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { ProductModule } from '@reqtrack/shared'

export interface ProductModuleEntry {
  module: string
  enabled: boolean
}

export function useProductModules(productId: string | undefined) {
  const { data, isSuccess } = useQuery({
    queryKey: ['productModules', productId],
    queryFn: () =>
      apiClient
        .get<{ data: ProductModuleEntry[] }>(`/products/${productId}/modules`)
        .then((r) => r.data.data),
    enabled: !!productId,
  })

  const enabledSet = new Set<ProductModule>(
    (data ?? [])
      .filter((m) => m.enabled)
      .map((m) => m.module as ProductModule)
  )

  return {
    /**
     * Returns true if the module is enabled.
     * Before module data has loaded (or if the endpoint doesn't exist yet),
     * returns true to avoid hiding nav items prematurely.
     */
    hasModule: (m: ProductModule) => !isSuccess || enabledSet.has(m),
    modules: data ?? [],
  }
}

export function useSetProductModule(productId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ module, enabled }: { module: string; enabled: boolean }) =>
      apiClient.put(`/products/${productId}/modules/${module}`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productModules', productId] })
    },
  })
}
